import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Upload as UploadIcon,
  FileSpreadsheet,
  Users,
  Calendar,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { employeeService } from "@/api/employees";
import { holidayService } from "@/api/holidays";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DocumentUploadProps {
  uploadType: "employee" | "holiday";
}

export function DocumentUpload({ uploadType }: DocumentUploadProps) {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isEmployee = uploadType === "employee";
  const title = isEmployee ? "Employee Data" : "Annual Calendar";
  const description = isEmployee
    ? "Upload employee information"
    : "Upload holiday calendar";
  const Icon = isEmployee ? Users : Calendar;
  const iconColorClass = isEmployee ? "text-primary" : "text-success";
  const iconBgClass = isEmployee ? "bg-primary/10" : "bg-success/10";

  const service = isEmployee ? employeeService : holidayService;

  const allowedTypes = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
    "application/vnd.ms-excel", // xls
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (
        !allowedTypes.includes(selectedFile.type) &&
        !selectedFile.name.match(/\.(csv|xlsx|xls)$/)
      ) {
        toast({
          title: "Invalid file type",
          description: "Please upload a CSV or Excel file",
          variant: "destructive",
        });
        e.target.value = "";
        setFile(null);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUploadClick = () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }
    setShowConfirm(true);
  };

  const processUpload = async (shouldDelete: boolean) => {
    if (!file) return;

    setLoading(true);
    setShowConfirm(false);

    try {
      if (shouldDelete) {
        await service.deleteAll();
      }

      await service.upload(file);

      toast({
        title: "Upload Successful",
        description: `Successfully processed ${file.name}${shouldDelete ? " (previous data wiped)" : ""}`,
      });
      setFile(null);
    } catch (error) {
      toast({
        title: "Upload Failed",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred during upload",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div
              className={`h-10 w-10 rounded-lg ${iconBgClass} flex items-center justify-center`}
            >
              <Icon className={`h-5 w-5 ${iconColorClass}`} />
            </div>
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${uploadType}-file`}>
              {isEmployee ? "Employee File" : "Holiday File"}
            </Label>
            <div className="flex gap-2">
              <Input
                id={`${uploadType}-file`}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
                className="flex-1"
                key={file ? "has-file" : "no-file"}
              />
              <Button onClick={handleUploadClick} disabled={loading || !file}>
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UploadIcon className="h-4 w-4 mr-2" />
                )}
                {loading ? "Processing..." : "Upload"}
              </Button>
            </div>
            {file && (
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
                Selected: {file.name}
              </p>
            )}
          </div>

          <div className="p-4 bg-muted rounded-lg space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Format Requirements
            </p>
            {isEmployee ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Accepted: .csv, .xlsx, .xls
                </p>
                <p className="text-xs text-muted-foreground">
                  Required columns: firstName, lastName, email
                </p>
                <p className="text-xs text-muted-foreground">
                  Optional columns: country, joiningDate, department, jobTitle, role, teamId, managerId
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Accepted: .csv, .xlsx, .xls
                </p>
                <p className="text-xs text-muted-foreground">
                  Required columns: date, name, type
                </p>
                <p className="text-xs text-muted-foreground">
                  Optional columns: country
                </p>
                <p className="text-xs text-muted-foreground">
                  Type values: public, optional
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Existing Data Found</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to wipe out all existing{" "}
              {isEmployee ? "employee" : "holiday"} data before uploading the
              new file? This action cannot be undone if you choose Yes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => processUpload(false)}>
              No, Append Data
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => processUpload(true)}>
              Yes, Wipe & Upload
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
